import AxiosClient from './AxiosClient';
import { handlePaginatedData } from '../services/DataHelperFunctions';
import { handleError, handleSuccess, handleApiErrorWithFallback } from './HttpUtil';
import { EAction, EApiType, EUrlParams } from '../data/Constants';
import type { Dispatch } from '@reduxjs/toolkit';
import type { IReportRequest } from '../../types/IApiTypes';
import { AppStore } from '../store';

/**
 * API client for report history retrieval, download and generation
 */
export const ReportApi = {
  /**
   * Get paginated report history from the api
   *
   * @param payload - Report request containing page, limit, reportType, optional searchCode and caller
   * @param dispatch - Redux dispatch function
   * @returns Paginated report history, or empty data/error object on failure
   */
  getReport: (payload: IReportRequest, dispatch: Dispatch) => {
    const queryParams = new URLSearchParams({
      [EUrlParams.Page]: String(payload.page),
      [EUrlParams.Limit]: String(payload.limit),
      reportType: payload.reportType,
    });
    if (payload.searchCode) {
      queryParams.set(EUrlParams.Search, payload.searchCode);
    }
    return AxiosClient.get(`/report/v1/history/read?${queryParams}`)
      .then((response) => handlePaginatedData(response, payload))
      .catch((err) =>
        handleApiErrorWithFallback(
          dispatch,
          err,
          { page: payload.page, searchKey: payload.searchCode, caller: payload.caller },
          EAction.Read,
          EApiType.ReportKey
        )
      );
  },

  /**
   * Download the generated report file as binary content.
   * Errors are dispatched to the store directly since no dispatch is passed in.
   *
   * @param payload - Object with the report history id and the report type
   * @param payload.id - Id of the report history record to download
   * @param payload.reportType - The type of report being downloaded
   */
  downloadReport: async (payload: { id: string; reportType: string }) => {
    AxiosClient.get(`/report/v1/download?reportId=${payload.id}&reportType=${payload.reportType}`, {
      responseType: 'arraybuffer',
    })
      .then((response) => {
        handleSuccess(response, EAction.Download);
      })
      .catch((err) => {
        handleError(AppStore.dispatch, err, EAction.Download);
      });
  },

  /**
   * Trigger report generation for the given report type
   *
   * @param action - Object containing the report type to generate
   * @param dispatch - Redux dispatch function
   * @returns Success response data, or error object on failure
   */
  generateReport: (action: { reportType: string }, dispatch: Dispatch) => {
    return AxiosClient.post(`/report/v1/history/create?reportType=${action.reportType}`)
      .then((response) => handleSuccess(response))
      .catch((err) => handleError(dispatch, err, EAction.CreateReport, EApiType.ReportKey));
  },
};
